import { Icon } from '@repo/ui';

import type { Invoice } from '../../types/invoice';
import type { AttorneyResponse, Timekeeper } from '../../types';
import { ANIMATION_DELAY_200MS_STYLE } from '../../utils/attorney-response';
import { AppealOptionSelector } from './AppealOptionSelector';
import { InvoiceDetails } from './InvoiceDetails';

export interface EditingResponseViewProps {
  editingTimekeeper?: Timekeeper;
  existingResponse: AttorneyResponse | null;
  selectedOption: 'appeal' | 'no-appeal';
  onBackToList: () => void;
  onOptionChange: (val: 'appeal' | 'no-appeal') => void;
  invoice: Invoice;
  totalAmount: number;
  amountDue: number;
  daysPastDue: number;
  renderAction: () => React.ReactNode;
  delegateSection?: React.ReactNode;
}

export const EditingResponseView: React.FC<EditingResponseViewProps> = ({
  editingTimekeeper,
  existingResponse,
  selectedOption,
  onBackToList,
  onOptionChange,
  invoice,
  totalAmount,
  amountDue,
  daysPastDue,
  renderAction,
  delegateSection,
}) => {
  return (
    <div className="flex flex-col gap-6 text-sm text-xtnd-dark dark:text-xtnd-light">
      <div className="opacity-0 animate-fade-in-right" style={ANIMATION_DELAY_200MS_STYLE}>
        <button
          onClick={onBackToList}
          type="button"
          className="text-xtnd-dark-500 dark:text-xtnd-blue border-none hover:bg-transparent hover:text-xtnd-blue focus:ring-0 focus:ring-offset-0 flex gap-1 items-center"
        >
          <Icon name="HiArrowLeft" size="xs" />
          <span className="text-sm font-medium -mt-1">Back to Responses</span>
        </button>
      </div>

      <div
        className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-start gap-3 shadow-sm animate-in slide-in-from-top duration-300 opacity-0 animate-fade-in-up"
        style={ANIMATION_DELAY_200MS_STYLE}
      >
        <div className="text-amber-700 dark:text-amber-400 mt-0.5">
          <Icon name="HiPencil" size="md" />
        </div>
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="text-sm font-bold text-amber-900 dark:text-amber-200">
                Editing Response
              </h4>
              {existingResponse ? (
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  You are editing <strong>{editingTimekeeper?.name}</strong>'s response. Changes
                  will overwrite the existing submission.
                </p>
              ) : (
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  You are acting on behalf of <strong>{editingTimekeeper?.name}</strong>. Please
                  review carefully before submitting.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {delegateSection}

      <AppealOptionSelector value={selectedOption} onValueChange={onOptionChange} />

      <InvoiceDetails
        invoice={invoice}
        totalAmount={totalAmount}
        amountDue={amountDue}
        daysPastDue={daysPastDue}
      />

      <div>{renderAction()}</div>
    </div>
  );
};
